import { Link } from "react-router";
import i18n, { type TFunction } from "i18next";
import {
  type Column,
  type ColumnDef,
  type RowData,
  type SortingFn,
  type Table as TableInstance,
} from "@tanstack/react-table";
import { ArrowUpDown } from "lucide-react";
import type { DocumentVersionSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { formatDate } from "~/helpers/date";
import { printFullName } from "~/helpers/user";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";
import MoreTooltip from "~/molecules/MoreTooltip";
import { FieldStatus } from "~/molecules/FieldStatus";
import { MyWorkflowStepsCell } from "~/molecules/MyWorkflowStepsCell";
import { TagsValue } from "~/molecules/Tags";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";
import type { EditableField } from "~/helpers/libraryTypes";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required for interface merging
  interface ColumnMeta<TData extends RowData, TValue> {
    label?: string;
  }
}

type DocumentsColumnId =
  | "title"
  | "status"
  | "tier"
  | "category"
  | "referenceNumber"
  | "tag"
  | "owner"
  | "links"
  | "publisher"
  | "publishedAt"
  | "adoptedAt"
  | "reviewFrom"
  | "reviewTo"
  | "mySteps"
  | "progress";

type DocumentsColumnGroupId = "metadata" | "workflow";
type DocumentsFeatureFlag = "referenceNumber";

interface DocumentsColumnSpec {
  id: DocumentsColumnId;
  getLabel: (t: TFunction) => string;
  group?: DocumentsColumnGroupId;
  editableField?: EditableField;
  featureFlag?: DocumentsFeatureFlag;
}

interface CreateDocumentsColumnsParams {
  t: TFunction;
  currentUserId: number;
  tagLabelById: Record<string, string>;
  filteredTagIds: string[];
  columnSpecs: DocumentsColumnSpec[];
}

interface ResolveDocumentsColumnMenuParams {
  columnSpecs: DocumentsColumnSpec[];
  table: TableInstance<DocumentVersionSummary>;
  t: TFunction;
}

interface ColumnBuilderContext {
  currentUserId: number;
  spec: DocumentsColumnSpec;
  t: TFunction;
  tagLabelById: Record<string, string>;
  filteredTagIds: Set<string>;
}

export interface ResolvedDocumentsColumnGroup {
  id: string;
  label: string;
  columns: Column<DocumentVersionSummary, unknown>[];
}

const columnGroupLabelById: Record<
  DocumentsColumnGroupId,
  (t: TFunction) => string
> = {
  metadata: (t) => t(($) => $.commons.document.metadata),
  workflow: (t) => t(($) => $.commons.workflows.workflow, { count: 1 }),
};

const documentsColumnSpecs: DocumentsColumnSpec[] = [
  {
    id: "referenceNumber",
    getLabel: (t) => t(($) => $.commons.document.referenceNumber),
    group: "metadata",
    editableField: "referenceNumber",
    featureFlag: "referenceNumber",
  },
  {
    id: "title",
    getLabel: (t) => t(($) => $.commons.document.title),
  },
  {
    id: "status",
    getLabel: (t) => t(($) => $.commons.document.status),
  },
  {
    id: "tier",
    getLabel: (t) => t(($) => $.commons.document.tier),
    group: "metadata",
    editableField: "tierId",
  },
  {
    id: "category",
    getLabel: (t) => t(($) => $.commons.document.category),
    group: "metadata",
    editableField: "categoryId",
  },
  {
    id: "tag",
    getLabel: (t) => t(($) => $.commons.document.tags),
    group: "metadata",
    editableField: "tags",
  },
  {
    id: "owner",
    getLabel: (t) => t(($) => $.commons.document.owner),
    group: "metadata",
    editableField: "ownerId",
  },
  {
    id: "links",
    getLabel: (t) => t(($) => $.commons.document.links),
    group: "metadata",
    editableField: "links",
  },
  {
    id: "publisher",
    getLabel: (t) => t(($) => $.commons.document.publisher),
    group: "workflow",
    editableField: "publisherId",
  },
  {
    id: "publishedAt",
    getLabel: (t) => t(($) => $.commons.document.publishedAt),
    group: "workflow",
  },
  {
    id: "adoptedAt",
    getLabel: (t) => t(($) => $.commons.document.adoptedAt),
    group: "workflow",
    editableField: "adoptedAt",
  },
  {
    id: "reviewFrom",
    getLabel: (t) => t(($) => $.commons.document.reviewFrom),
    group: "workflow",
    editableField: "reviewFrom",
  },
  {
    id: "reviewTo",
    getLabel: (t) => t(($) => $.commons.document.reviewTo),
    group: "workflow",
    editableField: "reviewTo",
  },
  {
    id: "mySteps",
    getLabel: (t) => t(($) => $.commons.workflow.mySteps),
    group: "workflow",
  },
  {
    id: "progress",
    getLabel: (t) => t(($) => $.commons.workflow.progress),
    group: "workflow",
  },
] as const;

const editableFieldLabelByField = Object.fromEntries(
  documentsColumnSpecs.flatMap((spec) =>
    spec.editableField ? [[spec.editableField, spec.getLabel]] : [],
  ),
) as Record<EditableField, (t: TFunction) => string>;

const naturalSort: SortingFn<DocumentVersionSummary> = (rowA, rowB, columnId) =>
  new Intl.Collator(i18n.language, { numeric: true }).compare(
    String(rowA.getValue(columnId) ?? ""),
    String(rowB.getValue(columnId) ?? ""),
  );

const SortableColumnHeader = ({
  column,
  label,
}: {
  column: Column<DocumentVersionSummary, unknown>;
  label: string;
}) => (
  <Button
    variant="ghost"
    className="h-auto p-0 hover:bg-transparent hover:underline"
    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
  >
    {label}
    {column.getIsSorted() === "asc" ? (
      "↑"
    ) : column.getIsSorted() === "desc" ? (
      "↓"
    ) : (
      <ArrowUpDown className="size-4 opacity-50" />
    )}
  </Button>
);

const sortableHeader: ColumnDef<DocumentVersionSummary>["header"] = ({
  column,
}) => (
  <SortableColumnHeader
    column={column}
    label={column.columnDef.meta?.label ?? ""}
  />
);

const columnBuilders: Record<
  DocumentsColumnId,
  (context: ColumnBuilderContext) => ColumnDef<DocumentVersionSummary>
> = {
  title: ({ spec, t }) => ({
    id: spec.id,
    accessorKey: "title",
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => (
      <Link
        to={`/documents/${row.original.document.id}?versionId=${row.original.id}&tab=metadata`}
        className="hover:underline"
      >
        {row.original.title}
      </Link>
    ),
  }),
  status: ({ spec, t }) => ({
    id: spec.id,
    accessorKey: "status",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => <FieldStatus status={row.original.status} />,
  }),
  tier: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.document.tier.title,
    cell: ({ row }) => row.original.document.tier.title,
  }),
  category: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.document.category.title,
    cell: ({ row }) => row.original.document.category.title,
  }),
  referenceNumber: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => row.document.referenceNumber ?? "",
    cell: ({ row }) => row.original.document.referenceNumber ?? "-",
  }),
  tag: ({ spec, t, tagLabelById, filteredTagIds }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => {
      const tags = row.document.tagIds
        .flatMap((tagId) => {
          const label = tagLabelById[tagId.toString()];
          return label ? [label] : [];
        })
        .sort();
      return tags.length > 0 ? tags.join(", ") : undefined;
    },
    sortUndefinedLast: true,
    cell: ({ row }) => {
      const tagEntries = row.original.document.tagIds.flatMap((tagId) => {
        const id = tagId.toString();
        const label = tagLabelById[id];
        return label ? [{ id, label }] : [];
      });
      const docTags = tagEntries
        .sort((a, b) => {
          const aMatch = filteredTagIds.has(a.id);
          const bMatch = filteredTagIds.has(b.id);
          if (aMatch !== bMatch) return aMatch ? -1 : 1;
          return a.label.localeCompare(b.label);
        })
        .map((tag) => tag.label);

      return (
        <div className="flex items-center gap-1">
          {docTags.length > 0 ? (
            <TagsValue>{docTags[0]}</TagsValue>
          ) : (
            <span>-</span>
          )}
          <MoreTooltip data={docTags} variant="tag" />
        </div>
      );
    },
  }),
  owner: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.document.owner),
    cell: ({ row }) => (
      <AvatarWithFallback
        user={row.original.document.owner}
        size="xs"
        showTooltip
      />
    ),
  }),
  links: ({ spec, t }) => ({
    id: spec.id,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) =>
      row.document.parentDocumentIds.length +
      row.document.childDocumentIds.length,
    cell: ({ row }) => {
      const totalLinks =
        row.original.document.parentDocumentIds.length +
        row.original.document.childDocumentIds.length;
      return totalLinks > 0 ? totalLinks : "-";
    },
  }),
  publisher: ({ spec, t }) => ({
    id: spec.id,
    sortingFn: naturalSort,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => printFullName(row.document.publisher),
    cell: ({ row }) =>
      row.original.document.publisher ? (
        <AvatarWithFallback
          user={row.original.document.publisher}
          size="xs"
          showTooltip
        />
      ) : (
        "-"
      ),
  }),
  publishedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.publishedAt ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.publishedAt),
  }),
  adoptedAt: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.adoptedAt ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.adoptedAt),
  }),
  reviewFrom: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.reviewFrom ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.reviewFrom),
  }),
  reviewTo: ({ spec, t }) => ({
    id: spec.id,
    accessorFn: (row) => row.reviewTo ?? undefined,
    sortingFn: "datetime",
    sortUndefined: "last",
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    cell: ({ row }) => formatDate(row.original.reviewTo),
  }),
  mySteps: ({ currentUserId, spec, t }) => ({
    id: spec.id,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) =>
      row.workflow?.steps.some((step) =>
        step.users.some((u) => u.userId === currentUserId),
      )
        ? 1
        : 0,
    cell: ({ row }) => (
      <MyWorkflowStepsCell
        steps={row.original.workflow?.steps}
        currentUserId={currentUserId}
      />
    ),
  }),
  progress: ({ spec, t }) => ({
    id: spec.id,
    meta: { label: spec.getLabel(t) },
    header: sortableHeader,
    accessorFn: (row) => {
      const total = row.workflow?.totalSteps ?? 0;
      return total === 0 ? 0 : (row.workflow?.completedSteps ?? 0) / total;
    },
    cell: ({ row }) => <WorkflowProgress workflow={row.original.workflow} />,
  }),
};

export const getDocumentsListColumnSpecs = (
  enabledFeatureFlags: Record<DocumentsFeatureFlag, boolean>,
): DocumentsColumnSpec[] =>
  documentsColumnSpecs.filter(
    (spec) => !spec.featureFlag || enabledFeatureFlags[spec.featureFlag],
  );

export const createDocumentsColumns = ({
  t,
  currentUserId,
  tagLabelById,
  filteredTagIds,
  columnSpecs,
}: CreateDocumentsColumnsParams): ColumnDef<DocumentVersionSummary>[] => {
  const filteredTagIdSet = new Set(filteredTagIds);
  return columnSpecs.map((spec) =>
    columnBuilders[spec.id]({
      currentUserId,
      spec,
      t,
      tagLabelById,
      filteredTagIds: filteredTagIdSet,
    }),
  );
};

export const getEditableFieldByColumnId = (
  columnSpecs: DocumentsColumnSpec[],
): Partial<Record<string, EditableField>> =>
  Object.fromEntries(
    columnSpecs.flatMap((spec) =>
      spec.editableField ? [[spec.id, spec.editableField]] : [],
    ),
  );

export const getEditableFieldLabel = (
  field: EditableField,
  t: TFunction,
): string => {
  const getLabel = editableFieldLabelByField[field];
  if (!getLabel) {
    throw new Error(`Missing label for editable field: ${field}`);
  }

  return getLabel(t);
};

export const resolveDocumentsColumnMenu = ({
  columnSpecs,
  table,
  t,
}: ResolveDocumentsColumnMenuParams): {
  columnMenuLabels: Record<string, string>;
  resolvedGroups: ResolvedDocumentsColumnGroup[];
  ungroupedColumns: Column<DocumentVersionSummary, unknown>[];
} => {
  const columnMenuLabels = Object.fromEntries(
    columnSpecs.map((spec) => [spec.id, spec.getLabel(t)]),
  );
  const hideableColumns = table
    .getAllColumns()
    .filter((column) => column.getCanHide());
  const groupedSpecs = (
    Object.keys(columnGroupLabelById) as DocumentsColumnGroupId[]
  ).map((groupId) => {
    const groupColumnIds = columnSpecs
      .filter((spec) => spec.group === groupId)
      .map((spec) => spec.id);

    return {
      id: groupId,
      label: columnGroupLabelById[groupId](t),
      columnIds: groupColumnIds,
    };
  });
  const groupedColumnIds = new Set<string>(
    groupedSpecs.flatMap((group) => group.columnIds),
  );
  const ungroupedColumns = hideableColumns.filter(
    (column) => !groupedColumnIds.has(column.id),
  );
  const resolvedGroups = groupedSpecs
    .map((group) => ({
      ...group,
      columns: group.columnIds
        .map((columnId) => table.getColumn(columnId))
        .filter((column): column is NonNullable<typeof column> => !!column),
    }))
    .filter((group) => group.columns.length > 0);

  return {
    columnMenuLabels,
    resolvedGroups,
    ungroupedColumns,
  };
};
